import { useMemo, ReactNode } from 'react';

// material-ui
import {
  StyledEngineProvider,
  createTheme,
  ThemeOptions,
  ThemeProvider,
  Theme,
  TypographyVariantsOptions,
} from '@mui/material/styles';

// project import
import config from 'config';
import Palette from 'themes/palette';
import Typography from 'themes/typography';

import componentStyleOverrides from 'themes/compStyleOverride';
import customShadows from 'themes/shadows';

// types
import { CustomShadowProps } from 'types/default-theme';

interface Props {
  children: ReactNode;
}

const { borderRadius, fontFamily, navType, outlinedFilled, presetColor } =
  config;

export default function ThemeCustomization({ children }: Props) {
  const theme: Theme = useMemo<Theme>(
    () => Palette(navType, presetColor),
    [navType, presetColor]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const themeTypography: TypographyVariantsOptions =
    useMemo<TypographyVariantsOptions>(
      () => Typography(theme, borderRadius, fontFamily),
      [theme, borderRadius, fontFamily]
    );
  const themeCustomShadows: CustomShadowProps = useMemo<CustomShadowProps>(
    () => customShadows(navType, theme),
    [navType, theme]
  );

  const themes: Theme = useMemo(() => {
    const themeOptions: ThemeOptions = {
      direction: 'ltr',
      palette: theme.palette,
      mixins: {
        toolbar: {
          minHeight: '48px',
          padding: '16px',
          '@media (min-width: 600px)': {
            minHeight: '48px',
          },
        },
      },
      typography: themeTypography,
      customShadows: themeCustomShadows,
    };

    const createdTheme = createTheme(themeOptions);
    const themeWithComponents = {
      ...createdTheme,
      components: componentStyleOverrides(
        createdTheme,
        borderRadius,
        outlinedFilled
      ),
    };

    return themeWithComponents as Theme;
  }, [
    theme,
    themeTypography,
    themeCustomShadows,
    borderRadius,
    outlinedFilled,
  ]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes}>{children}</ThemeProvider>
    </StyledEngineProvider>
  );
}
